import { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";

export default function BarChart({ data, categories, yAxisTitle, singleColor, variant = "default", onCategoryClick, onCategoryRightClick }) {
  const chartRef = useRef(null);
  const rightClickHandledRef = useRef(false);

  useEffect(() => {
    const baseColors = ["#39C0C3", "#A9CF38", "#DAE254"];

    // If singleColor is true but not a color string, default to first baseColor
    const colorToUse =
      typeof singleColor === "string"
        ? singleColor
        : singleColor
        ? baseColors[0]
        : null;

    // Decide colors: either single color or distributed
    const colors = colorToUse
      ? [colorToUse] // use one color for all bars
      : data.map((_, index) => {
          const groupSize = Math.ceil(data.length / baseColors.length);
          return baseColors[Math.floor(index / groupSize)];
        });

    // Default configuration
    const defaultXAxisConfig = {
      categories,
      labels: {
        style: { 
          colors: colorToUse ? undefined : colors, 
          fontSize: "14px",
          fontFamily: "var(--tblr-body-font-family)"
        },
        rotate: -45
      }
    };

    // Wrapped text configuration with -90 rotation
    const wrappedXAxisConfig = {
      categories,
      labels: {
        style: { 
          colors: colorToUse ? undefined : colors, 
          fontSize: "14px",
          fontFamily: "var(--tblr-body-font-family)"
        },
        rotate: -90,
        rotateAlways: true,
        minHeight: 150,
        maxHeight: 200,
        offsetY: 0,
        trim: true,
        hideOverlappingLabels: false,
        formatter: function(value) {
          // Truncate text if it exceeds 20 characters
          if (!value) return value;
          const maxLength = 20;
          if (value.length > maxLength) {
            return value.substring(0, maxLength) + '...';
          }
          return value;
        }
      },
      tickPlacement: 'on'
    };

    const options = {
      series: [{ name: yAxisTitle || "Series", data }],
      chart: { 
        height: variant === "wrapped" ? 500 : 350, 
        type: "bar", 
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        events: {
          dataPointSelection: function(event, chartContext, config) {
            // Handle bar click (left-click only)
            if (onCategoryClick && chartRef.current) {
              let categoryIndex = config.dataPointIndex;
              
              if (categoryIndex !== null && categoryIndex !== undefined && categoryIndex >= 0 && categoryIndex < categories.length) {
                setTimeout(() => {
                  try {
                    if (chartRef.current && onCategoryClick) {
                      onCategoryClick(categories[categoryIndex], categoryIndex);
                    }
                  } catch (e) {
                    console.warn('Error in onCategoryClick callback:', e);
                  }
                }, 50);
              }
            }
          }
        }
      },
      colors,
      plotOptions: { 
        bar: { 
          columnWidth: "45%", 
          distributed: !colorToUse,
          borderRadius: 6,
          dataLabels: {
            enabled: false
          }
        } 
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      grid: {
        padding: variant === "wrapped" ? {
          bottom: 0,
          left: 10,
          right: 10
        } : {
          bottom: 10,
          left: 10,
          right: 10
        }
      },
      xaxis: variant === "wrapped" ? wrappedXAxisConfig : defaultXAxisConfig,
      tooltip: {
        x: {
          formatter: function(value, { dataPointIndex }) {
            return categories[dataPointIndex];
          }
        },
        y: {
          formatter: function(value) {
            return value;
          }
        }
      }
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();

    // SINGLE right-click handler using mousedown
    const handleRightClick = (e) => {
      if (e.button !== 2) return; // Only handle right mouse button
      if (!onCategoryRightClick) return;
      
      // Prevent multiple calls with ref
      if (rightClickHandledRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      rightClickHandledRef.current = true;
      setTimeout(() => {
        rightClickHandledRef.current = false;
      }, 500);
      
      e.preventDefault();
      e.stopPropagation();
      
      const target = e.target;
      if (!target || !chartRef.current) return;
      
      let categoryIndex = null;
      
      // Check if clicked on a bar
      const bar = target.closest('.apexcharts-bar-area, .apexcharts-bar');
      if (bar) {
        const allBars = chartRef.current.querySelectorAll('.apexcharts-bar-area');
        categoryIndex = Array.from(allBars).indexOf(bar);
      }
      
      // Check if clicked on x-axis label
      if (categoryIndex === null || categoryIndex === -1) {
        const xAxisTextsGroup = chartRef.current.querySelector('.apexcharts-xaxis-texts-g');
        if (xAxisTextsGroup && xAxisTextsGroup.contains(target)) {
          const xAxisLabels = xAxisTextsGroup.querySelectorAll('text');
          categoryIndex = Array.from(xAxisLabels).indexOf(target);
          
          // If direct match not found, try finding parent text element
          if (categoryIndex === -1) {
            const textElement = target.closest('text');
            if (textElement) {
              categoryIndex = Array.from(xAxisLabels).indexOf(textElement);
            }
          }
        }
      }
      
      // Execute callback if valid index found
      if (categoryIndex !== null && categoryIndex >= 0 && categoryIndex < categories.length) {
        onCategoryRightClick(categories[categoryIndex], categoryIndex);
      }
    };

    // Attach right-click handler and add cursor styling
    if (onCategoryRightClick && chartRef.current) {
      chartRef.current.addEventListener('mousedown', handleRightClick, true);
      
      // Add cursor pointer to bars and labels
      setTimeout(() => {
        if (chartRef.current) {
          const bars = chartRef.current.querySelectorAll('.apexcharts-bar-area');
          bars.forEach(bar => {
            bar.style.cursor = 'pointer';
          });
          
          const xAxisLabels = chartRef.current.querySelectorAll('.apexcharts-xaxis-texts-g text');
          xAxisLabels.forEach(label => {
            label.style.cursor = 'pointer';
            label.style.pointerEvents = 'auto';
          });
        }
      }, 500);
    }

    // Add click event listener to x-axis labels after chart is rendered
    if (onCategoryClick) {
      const attachLabelListeners = () => {
        const chartElement = chartRef.current;
        if (!chartElement) return false;
        
        const xAxisLabels = chartElement.querySelectorAll('.apexcharts-xaxis-texts-g text');
        if (xAxisLabels.length === 0) return false;
        
        xAxisLabels.forEach((label, index) => {
          if (index < categories.length && label) {
            label.style.cursor = 'pointer';
            label.style.pointerEvents = 'auto';
            
            // Add click listener (only for left-click)
            if (!label._hasClickListener) {
              label.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onCategoryClick && chartRef.current) {
                  setTimeout(() => {
                    try {
                      if (chartRef.current && onCategoryClick) {
                        onCategoryClick(categories[index], index);
                      }
                    } catch (err) {
                      console.warn('Error in onCategoryClick callback:', err);
                    }
                  }, 50);
                }
              }, true);
              label._hasClickListener = true;
            }
          }
        });
        
        return true;
      };
      
      let attempts = 0;
      const maxAttempts = 15;
      const tryAttach = () => {
        if (attachLabelListeners() || attempts >= maxAttempts) {
          return;
        }
        attempts++;
        setTimeout(tryAttach, 150);
      };
      
      setTimeout(tryAttach, 200);
      setTimeout(tryAttach, 1000);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.removeEventListener('mousedown', handleRightClick, true);
      }
      chart.destroy();
    };
  }, [data, categories, singleColor, yAxisTitle, variant, onCategoryClick, onCategoryRightClick]);

  return <div ref={chartRef}></div>;
}
